import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { NOTIFICATIONS_MAX_MARK } from '@/constants/residents/notifications';
import { NOTIFICATION_COLORS } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';
import { NotificationAvatar } from './NotificationAvatar';
import { NotificationEmptyState } from './NotificationEmptyState';

interface MobileNotificationsModalProps {
  visible: boolean;
  onClose: () => void;
  onSeeAll: () => void;
  onSettings?: () => void;
  onPropertyChatPress?: () => void;
}

/**
 * MobileNotificationsModal
 * 
 * Fullscreen modal that appears when tapping the notification bell on mobile.
 * Shows all notifications with options to go to settings.
 * Matches the fullscreen modal pattern used in MobileAutopayDetailsModal.
 */
export const MobileNotificationsModal: React.FC<MobileNotificationsModalProps> = ({
  visible,
  onClose,
  onSeeAll,
  onSettings,
  onPropertyChatPress,
}) => {
  const insets = useSafeAreaInsets();
  const { notifications, isLoading, isUnread, isPropertyChat, formatDateMMMdd, markFirstNAsRead, markPropertyChatAsViewed } = useNotifications();

  // Mark first N notifications as read when modal opens
  React.useEffect(() => {
    if (visible && Array.isArray(notifications) && notifications.length > 0) {
      markFirstNAsRead(notifications, NOTIFICATIONS_MAX_MARK);
    }
  }, [visible, notifications, markFirstNAsRead]);

  const handleSettings = () => {
    if (onSettings) {
      onClose();
      onSettings();
    }
  };

  const renderNotificationRow = ({ item }: { item: NotificationItem }) => {
    const unread = isUnread(item);
    const isPropertyChatItem = isPropertyChat(item);
    
    // Check if this is a marketplace notification
    const isMarketplaceFeedback = item.notificationType === 'marketplace_feedback';
    const isMarketplaceNotification = item.notificationType === 'marketplace_notification';
    const isMarketplace = isMarketplaceFeedback || isMarketplaceNotification;
    
    // Get listing image for marketplace notifications
    const listingImageUrl = isMarketplaceFeedback 
      ? item.feedbackRequest?.listing?.image 
      : isMarketplaceNotification 
        ? item.marketplaceNotification?.metadata?.listingImageUrl 
        : undefined;
    
    // Handle press based on notification type
    const handlePress = () => {
      // Optimistically mark property chat as viewed for instant UI feedback
      if (isPropertyChatItem) {
        markPropertyChatAsViewed();
      }
      onClose();
      if (isPropertyChatItem && onPropertyChatPress) {
        onPropertyChatPress();
      } else {
        onSeeAll();
      }
    };
    
    // Determine avatar variant
    const avatarVariant = isPropertyChatItem ? 'message' : isMarketplace ? 'marketplace' : 'property';
    
    return (
      <TouchableOpacity
        style={styles.notificationRow}
        onPress={handlePress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${strings.notifications.mobile.defaultTitle}: ${item.title}`}
      >
        {/* Avatar - show appropriate icon based on notification type */}
        <NotificationAvatar 
          showUnreadDot={unread} 
          variant={avatarVariant}
          imageUrl={listingImageUrl}
        />

        {/* Text Content */}
        <View style={styles.textContainer}>
          <View style={styles.rowHeader}>
            <Text style={styles.title} numberOfLines={1}>
              {item.title || strings.notifications.mobile.defaultTitle}
            </Text>
            <Text style={styles.date}>{formatDateMMMdd(item.createdAt)}</Text>
          </View>
          <Text style={styles.description} numberOfLines={2}>
            {item.description || item.descriptionHtml || ''}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const keyExtractor = (item: NotificationItem) => `modal-notification-${item.id}`;

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={false}
      onRequestClose={onClose}
    >
      <View style={[styles.container, { paddingTop: insets.top }]}>
        {/* Modal Container with border - matches autopay/payment pattern */}
        <View style={styles.modalContent}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle}>{strings.notifications.mobile.modalTitle}</Text>
            </View>
            <View style={styles.headerRight}>
              {onSettings && (
                <TouchableOpacity
                  onPress={handleSettings}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button"
                  accessibilityLabel={strings.notifications.settings.title}
                  style={styles.headerButton}
                >
                  <RESIDENTS_SVG_COMPONENTS.COG
                    width={20}
                    height={20}
                    color={NOTIFICATION_COLORS.textSecondary}
                  />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                onPress={onClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel={`${strings.alert.cancel} ${strings.notifications.mobile.modalTitle.toLowerCase()}`}
                style={styles.headerButton}
              >
                <SVG_COMPONENTS.CANCEL_X
                  {...SVG_DIMENSIONS.CANCEL_X}
                  color={NOTIFICATION_COLORS.textSecondary}
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Body - Scrollable Content */}
          <View style={styles.body}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={NOTIFICATION_COLORS.textSecondary} />
                <Text style={styles.loadingText}>{strings.notifications.mobile.loading}</Text>
              </View>
            ) : !notifications || notifications.length === 0 ? (
              <NotificationEmptyState />
            ) : (
              <FlatList
                data={notifications}
                keyExtractor={keyExtractor}
                renderItem={renderNotificationRow}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.listContent}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
              />
            )}
          </View>

          {/* Footer */}
          <View style={[styles.footer, { paddingBottom: insets.bottom || 24 }]}>
            <TouchableOpacity
              style={styles.seeAllButton}
              onPress={() => {
                onClose();
                onSeeAll();
              }}
              accessibilityRole="button"
              accessibilityLabel={strings.notifications.mobile.seeAll}
            >
              <Text style={styles.seeAllText}>{strings.notifications.mobile.seeAll}</Text>
              <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
                width={20}
                height={20}
                color={NOTIFICATION_COLORS.linkBlue}
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NOTIFICATION_COLORS.screenBackground,
  },
  modalContent: {
    flex: 1,
    backgroundColor: NOTIFICATION_COLORS.cardBackground,
    borderWidth: 1,
    borderColor: NOTIFICATION_COLORS.modalBorder,
    borderRadius: 8,
    margin: 1,
    overflow: 'hidden',
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: NOTIFICATION_COLORS.borderSecondary,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 27,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  headerButton: {
    padding: 4,
  },

  // Body
  body: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textSecondary,
  },
  listContent: {
    paddingVertical: 8,
  },
  separator: {
    height: 1,
    backgroundColor: NOTIFICATION_COLORS.borderSecondary,
    marginHorizontal: 24,
  },

  // Notification Row
  notificationRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingVertical: 16,
    gap: 16,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
    flex: 1,
    marginRight: 8,
  },
  date: {
    fontSize: 12,
    fontWeight: '300',
    color: NOTIFICATION_COLORS.textSecondary,
    lineHeight: 18,
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Footer
  footer: {
    borderTopWidth: 1,
    borderTopColor: NOTIFICATION_COLORS.borderSecondary,
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: 'center',
  },
  seeAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 14,
    fontWeight: '500',
    color: NOTIFICATION_COLORS.linkBlue,
    lineHeight: 21,
  },
});

export default MobileNotificationsModal;
